import React, { useCallback, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

interface HoverHintProps {
  /** 提示文案；为空时不显示气泡 */
  hint?: string;
  cursor?: React.CSSProperties['cursor'];
  children: React.ReactNode;
}

/**
 * 禁用按钮的即时 hover 提示（原生 title 有 ~1s 延迟）。
 * 气泡 portal 到 body 并用 fixed 定位，避免被容器 overflow 裁剪或下方元素遮挡。
 * 禁用元素不派发鼠标事件：子按钮需设 pointer-events:none，由本包裹层接管 hover。
 */
export default function HoverHint({ hint, cursor, children }: HoverHintProps) {
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const wrapRef = useRef<HTMLSpanElement>(null);

  const show = useCallback(() => {
    if (!hint) return;
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPos({ left: rect.left + rect.width / 2, top: rect.bottom + 6 });
  }, [hint]);

  const hide = useCallback(() => setPos(null), []);

  return (
    <span
      ref={wrapRef}
      data-hint={hint || undefined}
      style={{ display: 'inline-flex', cursor }}
      onMouseEnter={show}
      onMouseLeave={hide}
    >
      {children}
      {hint && pos && ReactDOM.createPortal(
        <div
          role="tooltip"
          style={{
            position: 'fixed',
            left: pos.left,
            top: pos.top,
            transform: 'translateX(-50%)',
            background: 'var(--surface)',
            color: 'var(--text-2)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            padding: '4px 10px',
            fontSize: '0.78rem',
            whiteSpace: 'nowrap',
            zIndex: 3000,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            pointerEvents: 'none',
          }}
        >
          {hint}
        </div>,
        document.body,
      )}
    </span>
  );
}
